<template>
    <div class="pb-color-picker" :class="{ open }">
        <div v-if="hover" class="pb-color-face-toggle">
            <button type="button" :class="{ active: face === 'normal' }" @click="face = 'normal'">Normal</button>
            <button type="button" :class="{ active: face === 'hover' }" @click="face = 'hover'">Hover</button>
        </div>
        <div ref="control" class="pb-color-control">
            <button type="button" class="pb-color-swatch" :class="{ 'not-set': !swatchColor }" :style="swatchColor ? { background: swatchColor } : null" title="Choose color" @click="toggle"></button>
            <input type="text" class="pb-color-hex" :value="activeValue" placeholder="Set" @input="emitVal($event.target.value)" />
            <a v-if="activeValue" class="pb-color-clear" title="Unset" role="button" @click="emitVal('')"><i class="fas fa-times-circle"></i></a>
        </div>

        <Teleport to="body">
            <div v-if="open" ref="dropdown" class="pb-color-dropdown" :style="dropdownStyle">
                <div class="pb-color-search">
                    <input ref="search" v-model="search" type="text" class="form-control form-control-sm" placeholder="Search colors" />
                </div>
                <div class="pb-color-list">
                    <button v-for="c in filteredColors" :key="c.value" type="button" class="pb-color-option" :class="{ active: activeValue === c.value }" @click="selectTheme(c.value)">
                        <span class="pb-color-dot" :style="{ background: c.value }"></span>
                        <span class="pb-color-name">{{ c.name }}</span>
                    </button>
                    <div v-if="!filteredColors.length" class="pb-color-empty">No colors found</div>
                </div>
                <button type="button" class="pb-color-option pb-color-custom" @click="openCustom">
                    <span class="pb-color-dot pb-color-dot-custom"></span>
                    <span class="pb-color-name">Custom</span>
                </button>
            </div>
        </Teleport>

        <input ref="native" type="color" class="pb-native-color" :value="customHex" @input="emitVal($event.target.value)" />
    </div>
</template>

<script>
// Heygov-style color picker (swatch + hex + searchable theme-color dropdown + custom),
// skinned to the old dashboard look. When `hover` is true a Normal/Hover toggle flips the
// single picker between the base value (emits `change`) and the hover value (emits
// `changeHover`) — same control for both, like heygov. Theme colors come from the same
// source as the old ColorChooser (themeSettings codes ending in _COLOR -> var(--code)).
export default {
    name: "ColorPicker",
    props: {
        value: { type: String, default: "" },
        hoverValue: { type: String, default: "" },
        hover: { type: Boolean, default: false },
        themeSettings: { type: Array, default: () => [] },
    },
    emits: ["change", "changeHover"],
    data() {
        return { open: false, search: "", dropdownStyle: {}, face: "normal" };
    },
    computed: {
        colors() {
            const out = [];
            (this.themeSettings || []).forEach((option) => {
                if (option.code && option.code.includes("_COLOR")) {
                    out.push({ name: option.code.replace(/_/g, " "), value: `var(--${option.code.toLowerCase().replace(/_/g, "-")})` });
                }
            });
            return out;
        },
        filteredColors() {
            const q = this.search.trim().toLowerCase();
            if (!q) return this.colors;
            return this.colors.filter((c) => c.name.toLowerCase().includes(q));
        },
        activeValue() {
            return this.face === "hover" ? this.hoverValue : this.value;
        },
        swatchColor() {
            return this.activeValue ? this.activeValue : null;
        },
        customHex() {
            return /^#([0-9a-fA-F]{3,8})$/.test(this.activeValue) ? this.activeValue : "#000000";
        },
    },
    beforeUnmount() {
        this.detachListeners();
    },
    methods: {
        emitVal(val) {
            this.$emit(this.face === "hover" ? "changeHover" : "change", val);
        },
        toggle() {
            this.open ? this.close() : this.openDropdown();
        },
        openDropdown() {
            this.reposition();
            this.search = "";
            this.open = true;
            document.addEventListener("pointerdown", this.onOutsideClick, true);
            window.addEventListener("scroll", this.reposition, true);
            window.addEventListener("resize", this.reposition);
        },
        reposition() {
            if (!this.$refs.control) return;
            const rect = this.$refs.control.getBoundingClientRect();
            this.dropdownStyle = { position: "fixed", top: `${rect.bottom + 4}px`, left: `${rect.left}px`, width: `${Math.max(rect.width, 200)}px` };
        },
        close() {
            this.open = false;
            this.detachListeners();
        },
        detachListeners() {
            document.removeEventListener("pointerdown", this.onOutsideClick, true);
            window.removeEventListener("scroll", this.reposition, true);
            window.removeEventListener("resize", this.reposition);
        },
        onOutsideClick(event) {
            const inControl = this.$el.contains(event.target);
            const inDropdown = this.$refs.dropdown && this.$refs.dropdown.contains(event.target);
            if (!inControl && !inDropdown) this.close();
        },
        selectTheme(value) {
            this.emitVal(value);
            this.close();
        },
        openCustom() {
            this.close();
            this.$refs.native && this.$refs.native.click();
        },
    },
};
</script>

<style scoped>
/* old-dashboard skin: soft box-shadow pill, no flat borders */
.pb-color-picker {
    position: relative;
    display: inline-block;
}
.pb-color-face-toggle {
    display: inline-flex;
    margin-bottom: 4px;
    border-radius: 4px;
    overflow: hidden;
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.12);
}
.pb-color-face-toggle button {
    border: none;
    background: #e6eaed;
    color: #555;
    font-size: 11px;
    padding: 3px 10px;
    cursor: pointer;
}
.pb-color-face-toggle button.active {
    background: #fff;
    color: #333;
    font-weight: 600;
}
.pb-color-control {
    display: flex;
    align-items: center;
    gap: 4px;
    background: #fff;
    border-radius: 4px;
    height: 31.5px;
    padding: 2px 4px;
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.12);
    min-width: 275px;
}
.pb-color-swatch {
    position: relative;
    flex: 0 0 auto;
    width: 24px;
    height: 24px;
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 4px;
    cursor: pointer;
    padding: 0;
    background: #fff;
    outline: none;
}
.pb-color-swatch.not-set::after {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) rotate(-45deg);
    width: 2px;
    height: 18px;
    background-color: #ef4444;
    pointer-events: none;
}
.pb-color-hex {
    flex: 1;
    min-width: 0;
    background: transparent;
    border: none;
    padding: 0 6px;
    height: 26px;
    font-size: 13px;
    color: #333;
    outline: none;
}
.pb-color-clear {
    color: #dc3545;
    font-size: 12px;
    cursor: pointer;
    padding-right: 4px;
}
.pb-color-dropdown {
    z-index: 3000;
    display: flex;
    flex-direction: column;
    max-height: 280px;
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
    padding: 4px;
}
.pb-color-search {
    padding: 2px 2px 6px;
}
.pb-color-list {
    flex: 1 1 auto;
    overflow-y: auto;
    min-height: 0;
}
.pb-color-empty {
    padding: 8px;
    font-size: 13px;
    color: #9ca3af;
    text-align: center;
}
.pb-color-option {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    border: none;
    background: transparent;
    padding: 6px 8px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 13px;
    color: #333;
    text-align: left;
}
.pb-color-option:hover {
    background: #f3f4f6;
}
.pb-color-option.active {
    background: #eff6ff;
    color: #2563eb;
}
.pb-color-dot {
    flex: 0 0 auto;
    width: 18px;
    height: 18px;
    border-radius: 4px;
    border: 1px solid rgba(0, 0, 0, 0.1);
}
.pb-color-dot-custom {
    background: conic-gradient(#f00, #ff0, #0f0, #0ff, #00f, #f0f, #f00);
}
.pb-color-custom {
    border-top: 1px solid #f1f2f4;
    margin-top: 4px;
    padding-top: 8px;
}
.pb-native-color {
    position: absolute;
    width: 0;
    height: 0;
    padding: 0;
    border: 0;
    opacity: 0;
    pointer-events: none;
    bottom: 0;
    left: 24px;
}
</style>
